<template>
  <div class="content">
    <div class="container-fluid">
      <b-navbar type="light" variant="white" sticky class="navbar-enhanced shadow-sm mb-4">
        <b-navbar-nav class="w-100 align-items-center justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <b-button
              pill
              variant="secondary"
              :to="urlBack()"
              v-if="hasBack()"
              class="mr-3"
            >
              <b-icon icon="caret-left" aria-hidden="true"></b-icon>
              Retour
            </b-button>
            <div class="account-pill d-flex align-items-center">
              <div class="account-avatar text-uppercase mr-3">
                {{ accountInitials }}
              </div>
              <div>
                <p class="mb-0 account-name">{{ account.name }}</p>
                <small class="text-muted">{{ company.name }}</small>
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <div class="text-right mr-3 d-none d-md-block">
              <small class="text-muted d-block">Statut compte</small>
              <b-badge :variant="statusVariant(company._status)">
                {{ statusLabel(company._status) }}
              </b-badge>
            </div>
            <b-button
              variant="primary"
              class="mr-2"
              :to="urlWithRedirect('/ppe_eu/account/'+(account.id))"
            >
              <b-icon icon="pencil-fill" class="mr-1"></b-icon>
              Modifier
            </b-button>
            <b-dropdown right no-caret variant="outline-secondary" toggle-class="btn btn-outline-secondary rounded-pill">
              <template #button-content>
                <b-icon icon="three-dots-vertical" aria-hidden="true"></b-icon>
              </template>
              <b-dropdown-item @click="changeCompany()">
                <b-icon icon="building" variant="primary" class="mr-2"></b-icon>
                Changer la fiche signalétique
              </b-dropdown-item>
              <b-dropdown-item
                v-if="isAdmin()"
                @click="deleteWithConfirm('')"
              >
                <b-icon icon="dash-circle" variant="danger" class="mr-2"></b-icon>
                Supprimer ?
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-navbar-nav>
      </b-navbar>

      <!-- B1. Fiche de présentation -->
      <div class="row" v-if="isPartnerOrManager()">
        <div class="col-xl-4 col-md-6 mb-4">
          <stats-card :style="getCardStyle(company._status)">
            <div slot="content">
              <h4 class="card-title">
                <status-icon :status="company._status" />
                Fiche {{ partnerOrManagerText() }}
              </h4>
              <p class="card-category">{{ company.name }}</p>
              <p class="card-category">{{ company.address }} {{ company.zip }} {{ company.city }}</p>
              <h5 v-if="company.isHost" class="text-warning"></h5>
            </div>
            <div slot="footer">
              <i class="fa fa-calendar-o"></i> {{ toDate(company._modified) }}
              <router-link :to="urlWithRedirect('/ppe_eu/company_eu/'+(company.id))" active-class="active">Modifier</router-link>
            </div>
          </stats-card>
        </div>
        <div class="col-xl-8 col-md-6 mb-4">
          <b-card class="resume-card h-100">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="mb-0">Résumé compte</h5>
              <b-badge :variant="statusVariant(company._status)">
                {{ statusLabel(company._status) }}
              </b-badge>
            </div>
            <b-row>
              <b-col md="6" class="mb-3">
                <div class="info-line">
                  <b-icon icon="geo-alt" class="mr-2 text-muted"></b-icon>
                  <span>{{ company.address }} {{ company.zip }} {{ company.city }}</span>
                </div>
              </b-col>
              <b-col md="6" class="mb-3">
                <div class="info-line">
                  <b-icon icon="people" class="mr-2 text-muted"></b-icon>
                  <span>{{ partnerOrManagerText() }}</span>
                </div>
              </b-col>
              <b-col md="6" class="mb-3">
                <div class="info-line">
                  <b-icon icon="building" class="mr-2 text-muted"></b-icon>
                  <span>{{ companies.length }} entité(s) suivie(s)</span>
                </div>
              </b-col>
              <b-col md="6" class="mb-3">
                <div class="info-line">
                  <b-icon icon="folder-check" class="mr-2 text-muted"></b-icon>
                  <span>{{ operations.length }} opération(s)</span>
                </div>
              </b-col>
            </b-row>
            <div class="mt-2">
              <div class="d-flex justify-content-between mb-1">
                <small class="text-muted">Complétion</small>
                <small>{{ companyProgress }}%</small>
              </div>
              <b-progress
                height="8px"
                :value="companyProgress"
                :variant="statusVariant(company._status)"
              ></b-progress>
            </div>
          </b-card>
        </div>
      </div>

      <div class="row" v-for="(company, index) in companies" :key="index">
        <h5 class="col-12 company-collapse-header" v-b-toggle="'collapse-'+index">
          <span class="when-opened">
            <i class="fa fa-chevron-down" aria-hidden="true"></i>
          </span>
          <span class="when-closed">
            <i class="fa fa-chevron-right" aria-hidden="true"></i>
          </span>
          <div class="d-flex align-items-center">
            <b>{{ index + 1 }} - {{ company.name }}</b>
            <b-badge class="ml-3" :variant="statusVariant(company._status)">
              {{ statusLabel(company._status) }}
            </b-badge>
          </div>
        </h5>
        <!-- B1. Fiche de présentation -->
        <b-collapse :visible="isCollapsibleOpen(company.id)" :id="'collapse-'+index" class="col-12">
          <div class="row">
            <div class="col-xl-4 col-md-6">
              <stats-card :style="getCardStyle(company._status)">
                <div slot="content">
                  <h4 class="card-title"><status-icon :status="company._status" /> B1. Fiche de présentation</h4>
                  <p class="card-category">{{ company.name }}</p>
                  <p class="card-category">{{ company.address }} {{ company.zip }} {{ company.city }}</p>
                  <h5 v-if="company.isHost" class="text-warning"></h5>
                </div>
                <div slot="footer">
                  <i class="fa fa-calendar-o"></i> {{ toDate(company._modified) }}
                  <router-link :to="urlWithRedirect('/ppe_eu/company_eu/'+(company.id), 'companyid', company.id)" active-class="active">Modifier</router-link>
                </div>
              </stats-card>
            </div>

            <!-- B2. Sources de dangers -->
            <div class="col-xl-4 col-md-6">
              <stats-card :style="getCardStyle(company.b2._status)">
                <div slot="content">
                  <h4 class="card-title"><status-icon :status="company.b2._status" /> B2. Sources de dangers</h4>
                  <p class="card-category">D’après les informations recueillies dans votre Document Unique</p>
                </div>
                <div slot="footer">
                  <i v-if="company.b2" class="fa fa-calendar-o"></i> {{ company.b2 ? toDate(company.b2._modified) : '' }}
                  <router-link v-if="company.b2" :to="urlWithRedirect('/ppe_eu/b2/'+company.b2.id+'?companyid='+company.id, 'companyid', company.id)">Modifier</router-link>
                  <router-link v-if="!company.b2" :to="urlWithRedirect('/ppe_eu/b2/?companyid='+company.id, 'companyid', company.id)">Compléter</router-link>
                </div>
              </stats-card>
            </div>

            <!-- B3. Livret d'Accueil Prévention -->
            <div class="col-xl-4 col-md-6">
              <stats-card :style="getCardStyle(company.b3._status)">
                <div slot="content">
                  <h4 class="card-title"><status-icon :status="company.b3._status" /> B3. Livret d'Accueil Prévention</h4>
                  <p class="card-category">Analyse des principaux dangers</p>
                </div>
                <div slot="footer">
                  <i v-if="company.b3" class="fa fa-calendar-o"></i> {{ toDate(company.b3._modified) }}
                  <router-link :to="urlWithRedirect('/ppe_eu/b3/'+company.b3.id+'?companyid='+company.id, 'companyid', company.id)">Modifier</router-link>
                </div>
              </stats-card>
            </div>
          </div>
        </b-collapse>
      </div>

      <div class="floating-action-bar shadow-sm border rounded" v-if="account.id">
        <b-button
          variant="outline-primary"
          class="mr-2"
          :to="urlWithRedirect('/ppe_eu/company_eu/?newForUser='+account.id)"
        >
          <b-icon icon="plus-circle" class="mr-1"></b-icon>
          Ajouter une entité (société, pôle, ...)
        </b-button>
        <b-button variant="outline-secondary" @click="changeCompany()">
          <b-icon icon="arrows-move" class="mr-1"></b-icon>
          Changer la fiche signalétique
        </b-button>
      </div>
    </div>
    <ChangeCompany ref="changeCompany" />
  </div>
</template>

<script>
  //import ChartCard from 'src/components/Cards/ChartCard.vue'
  import ppeBusiness from 'ppe-business'
  import StatsCard from '../../components/Cards/StatsCard.vue'
  //import LTable from 'src/components/Table.vue'
  import StatusIcon from '../../components/StatusIcon.vue'
  import swal from 'sweetalert'
  const Business = require('ppe-business')

  import common from '../common/pages.js'
  import ChangeCompany from '../../components/Dialogs/ChangeCompany.vue'

  const STATUS_PROGRESS_ORDER = ['draft', 'in_progress', 'pending', 'validated', 'archived']

  export default {
    components: {
      StatsCard,
      StatusIcon,
      ChangeCompany
    },


    mounted() {
      this.refreshData()
    },


    data () {
      return {
        operationCode:'',
        account:{},
        company:{},
        b2:{},
        companies:[],
        operations: [],
        displayDone: false,

      }
    },

    computed: {
      accountInitials() {
        if (!this.account || !this.account.name) return ''
        return this.account.name
          .split(/\s+/)
          .filter(Boolean)
          .map(chunk => chunk.charAt(0))
          .join('')
          .substring(0, 2)
          .toUpperCase()
      },
      companyProgress() {
        return this.statusToProgress(this.company && this.company._status)
      }
    },
    methods:{
      ...common.methods,
      refreshData() {
        const url = this.getApiUrl()
        this.$http.get(url)
          .then(response => {
            this.operations           = response.data.operations
            this.company              = response.data.company

            this.account              = response.data.account
            this.companies            = response.data.companies

            this.$refs.changeCompany.init(this.company, this.account)
          })
          .catch( (err, res) => {
            // Clone the error to read the status property (otherwise it can only be read once !!!!)
            let error = JSON.parse( JSON.stringify(err) )
            swal("Erreur", JSON.stringify(err.response ? err.response.data : error.message), "error");
          })
      },
      isCollapsibleOpen(companyid) {
        if(this.$route.query.companyid)
          return companyid === this.$route.query.companyid
        return this.companies.length <= 2
      },

      OLD_router() {
        return this.$router
      },

      /**
       * "2022-02-12T23:45:41.452Z" => 12/fev/2022
       */
      toDate(apiDate) {
        return Business.toDate(apiDate)
      },

      getApiUrl() {
        let userId
        if(this.$route.params.accountid) userId = this.$route.params.accountid
        else userId = this.$usermanager.getCurrentUser()._id

        return '/eu/dashboardEU?id='+userId+"&done="+(this.displayDone?'1':'')
      },

      canDisplayOperations() {
        return Business.canDisplayOperations(this.account._status, this.company._status)
      },

      isHost() {
        return this.company.isHost
      },
      isAdmin() {
        return this.$usermanager.isAdmin()
      },
      isPartner() {
        return this.account.groups &&
          (this.account.groups.includes(Business.GROUP.PARTNER))
      },
      isPartnerOrManager() {
        return this.account.groups &&
          (this.account.groups.includes(Business.GROUP.PARTNER) ||
          this.account.groups.includes(Business.GROUP.MANAGER))
      },
      partnerOrManagerText() {
        if(this.account.groups && this.account.groups.includes(Business.GROUP.PARTNER))
          return Business.getGroupLabel(Business.GROUP.PARTNER)
        if(this.account.groups && this.account.groups.includes(Business.GROUP.MANAGER))
          return Business.getGroupLabel(Business.GROUP.MANAGER)
        return ''
      },

      async deleteWithConfirm() {
        var answer = await swal({
            title: 'Confirmez ?',
            text: `Vous allez supprimer le compte ${this.account.name} et l'Entreprise Utilisatrice ${this.company.name} ainsi que toutes ses opérations \n`,
            type: 'warning',

            buttons: {
                  cancel:  "Non, annuler",
                  confirm: {text:"Oui, supprimer tout", className:"btn btn-danger"},
            },
          })
          if(answer) {
            try {
              await this.$http.get("/admin/delete_account?accountid="+this.account.id);
              this.$router.push(this.urlBack())
            } catch(err) {
              swal("Erreur", JSON.stringify( err.response ? err.response.data : err.message), "error");
            }
          }
      },

      async changeCompany() {
        this.$refs.changeCompany.showChangeCompanyDialog();
      },
      statusVariant(status) {
        if (!status) return 'secondary'
        const normalized = status.toString().toLowerCase()
        const variants = {
          draft: 'secondary',
          pending: 'warning',
          in_progress: 'info',
          progress: 'info',
          validated: 'success',
          done: 'success',
          completed: 'success',
          archived: 'dark',
          cancelled: 'secondary',
          alert: 'danger'
        }
        return variants[normalized] || 'secondary'
      },
      statusLabel(status) {
        if (!status) {
          return 'Non défini'
        }
        return ppeBusiness.getStatusLabel(status)
      },
      statusToProgress(status) {
        if (!status) return 0
        const normalized = status.toString().toLowerCase()
        const index = STATUS_PROGRESS_ORDER.indexOf(normalized)
        if (index === -1) {
          return 60
        }
        return Math.round(((index + 1) / STATUS_PROGRESS_ORDER.length) * 100)
      }
    },


  }
</script>
<style>
  .collapsed > .when-opened,
  :not(.collapsed) > .when-closed {
    display: none;
  }

  .navbar-enhanced {
    border-radius: 0.5rem;
    background-color: #fff;
  }

  .account-pill {
    padding: 0.75rem 1rem;
    border-radius: 999px;
    background-color: #f7f9fc;
  }

  .account-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #e1e7ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }

  .account-name {
    font-weight: 600;
  }

  .resume-card {
    border: none;
    border-radius: 0.75rem;
    box-shadow: 0 0.75rem 1rem rgba(0, 0, 0, 0.05);
  }

  .resume-card .info-line {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
  }

  .company-collapse-header {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    background: #f8f9fc;
    margin-bottom: 0.25rem;
  }

  .floating-action-bar {
    padding: 1rem;
    margin: 1.5rem 0;
    background: #fff;
  }

</style>
